import Dialog from "@mui/material/Dialog";
import { useId, useState } from "react";
import { GfgButton } from "@/components/ui/GfgButton";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { COPY } from "@/constants/sendQueueCampaigns";
import type { SendQueueItem } from "@/interfaces/sendQueueCampaigns";
import { reminderLabel, rescheduleError } from "@/utils/sendQueueCampaigns-helper";

export interface RescheduleModalProps {
  /** The row to reschedule. The panel mounts this component only while its dialog is the open one
   *  (`dialog === "reschedule" && activeItem && <RescheduleModal .../>`), so `item` is never
   *  absent and never switches to a different row for the component's lifetime — closing
   *  unmounts it outright rather than toggling it hidden. */
  item: SendQueueItem;
  onClose: () => void;
  /** Confirms the new time as an ISO string. The modal never calls the API — the caller owns the
   *  mutation, which validates the same rules server-side and returns 400 on a stale/bad time. */
  onSave: (iso: string) => void;
}

/** `iso` -> the value a native `<input type="datetime-local">` expects, in local time. */
function toDatetimeLocalValue(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The reverse of `toDatetimeLocalValue` — `null` for an empty or unparseable field, since a
 *  blank/invalid native input value can't be turned into `new Date(...).toISOString()` without
 *  throwing. */
function toIsoOrNull(localValue: string): string | null {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * The reschedule form (unit U11, GFG-194) — its own MUI Dialog rather than GfgModal, since this
 * is a form, not a yes/no confirm. Native `<input type="datetime-local">` is deliberate: this repo
 * has no `@mui/x-date-pickers` installed and already uses native date inputs elsewhere (see
 * constants/dates.ts). Appearance follows D21 — Tailwind + design tokens for the new layout, MUI
 * kept to the Dialog behaviour primitive (focus trap, portal, Escape/backdrop, scroll lock).
 *
 * Validation runs client-side via `rescheduleError` (utils/sendQueueCampaigns-helper.ts) purely for
 * feedback — Save is blocked while it returns non-null, but the server re-validates the same
 * rules independently and is the actual security boundary.
 */
export default function RescheduleModal({ item, onClose, onSave }: RescheduleModalProps) {
  // Pre-filled once, from the row's scheduled time at mount. The panel mounts a fresh instance
  // per open (see the prop comment), so there is no later `item` to react to and no in-progress
  // edit that a background refetch could ever blow away.
  const [value, setValue] = useState(() => toDatetimeLocalValue(item.scheduledAt));

  const subtitleId = useId();
  const fieldId = useId();
  const errorId = useId();

  const parsedIso = toIsoOrNull(value);
  const error = parsedIso ? rescheduleError(item, parsedIso, Date.now()) : null;
  const canSave = parsedIso !== null && error === null;

  function handleSave() {
    if (!canSave || !parsedIso) return;
    onSave(parsedIso);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={subtitleId}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 440,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      <p id={subtitleId} className="mb-5 font-lato text-graphite text-sm">
        {reminderLabel(item)} · {item.campaignTitle}
      </p>

      <label
        htmlFor={fieldId}
        className="mb-1.5 block font-bold font-lato text-[11px] text-graphite uppercase tracking-[1.5px]"
      >
        {COPY.reschedule.label}
      </label>
      <input
        id={fieldId}
        type="datetime-local"
        lang={DATE_INPUT_LANG}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`w-full rounded-lg border bg-white px-3.5 py-2.5 font-lato text-rodeo-black text-sm focus:outline-none focus:ring-1 ${
          error
            ? "border-good-wine focus:border-good-wine focus:ring-good-wine"
            : "border-smoke focus:border-dust-ink focus:ring-dust-ink"
        }`}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 font-lato text-good-wine text-xs">
          {error}
        </p>
      )}

      <p className="mt-3 font-lato text-graphite text-xs">{COPY.reschedule.note}</p>

      <div className="mt-6 flex items-center justify-end gap-3">
        <GfgButton type="button" variant="secondary" onClick={onClose}>
          {COPY.modal.cancel}
        </GfgButton>
        <GfgButton type="button" variant="primary" disabled={!canSave} onClick={handleSave}>
          {COPY.modal.save}
        </GfgButton>
      </div>
    </Dialog>
  );
}
